import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { client } from '@frontend/features/common/services';
import { CONFIG } from '@frontend/config';
import { catalogAdminKey, catalogAdminPath } from './queries';
import { DiscoveryPage, discoveryCsvCell, discoveryPending } from './discovery';
import {
  AdoptionAction,
  AdoptionFilters,
  AdoptionHotel,
  AdoptionJob,
  AdoptionPreviewHotel,
  AdoptionPreviewPage,
  AdoptionSelection,
  IncorporationIntent,
  IncorporationSession,
  adoptionCsvIds,
  adoptionNeedsDecision,
  adoptionReason,
  adoptionSelected,
  toggleAdoptionSelection,
  incorporationRequest,
} from './adoption';
import { DestinationPicker } from './DestinationPicker';
import { Candidate, CandidatePicker } from './AddSelection';
const root = 'hotel-catalog-admin/adoptions';
type ReviewRow = AdoptionPreviewHotel | AdoptionHotel;
const rowReason = (r: ReviewRow) => ('state' in r ? r.reason : adoptionReason(r));
const reviewable = (r: ReviewRow) =>
  ['possible_duplicate', 'insufficient_identity', 'destination_unresolved', 'conflicting_identifiers'].includes(
    rowReason(r) || '',
  );

export function CatalogAdoption({
  discoveryId,
  active,
  initialFilters = {},
  destinationLabel = '',
  country,
}: {
  discoveryId: string;
  active: boolean;
  initialFilters?: AdoptionFilters;
  destinationLabel?: string;
  country: string;
}) {
  const { t } = useTranslation();
  const l = (k: string, o?: Record<string, unknown>) => t(`hotelIncorporation.${k}`, o);
  const navigate = useNavigate(),
    cache = useQueryClient();
  const [params, setParams] = useSearchParams();
  const storageKey = `catalog-incorporation:${CONFIG.AGENCY_ID}:${discoveryId}`;
  const legacyStorageKey = `catalog-adoption-pending:${CONFIG.AGENCY_ID}:${discoveryId}`;
  const [session, setSession] = useState<IncorporationSession | null>(() => {
    try {
      const legacy = JSON.parse(sessionStorage.getItem(legacyStorageKey) || 'null');
      if (legacy?.discovery_job_id === discoveryId) return { request: legacy };
      const s = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      return s?.request?.discovery_job_id === discoveryId ? s : null;
    } catch {
      return null;
    }
  });
  const [showResult, setShowResult] = useState(!!session?.jobId),
    [jobId, setJobId] = useState(session?.jobId || '');
  const [filters, setFilters] = useState(initialFilters),
    [q, setQ] = useState(initialFilters.q || ''),
    [offset, setOffset] = useState(0);
  const [destination, setDestination] = useState<Candidate | null>(
    initialFilters.geo_place_id
      ? {
        id: initialFilters.geo_place_id,
        label: destinationLabel || initialFilters.geo_place_id,
        country_code: country,
        locality: null,
      }
      : null,
  );
  const [selection, setSelection] = useState<AdoptionSelection>({ mode: 'all' }),
    [error, setError] = useState(''),
    [review, setReview] = useState<ReviewRow | null>(null);
  const [resultFilter, setResultFilter] = useState(''),
    [resultOffset, setResultOffset] = useState(0),
    [exporting, setExporting] = useState(false);
  const saving = useRef(false),
    exportAbort = useRef<AbortController | null>(null),
    intentRunning = useRef(false);
  useEffect(() => () => exportAbort.current?.abort(), []);
  const persist = (next: IncorporationSession | null) => {
    try {
      if (next) sessionStorage.setItem(storageKey, JSON.stringify(next));
      else sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(legacyStorageKey);
      setSession(next);
      return true;
    } catch {
      setError('storageError');
      return false;
    }
  };
  const history = useQuery({
    queryKey: [...catalogAdminKey, 'adoptions', discoveryId],
    enabled: active,
    retry: false,
    queryFn: async () =>
      (
        await client.get<{ data: Pick<AdoptionJob, 'job_id' | 'status' | 'created_at'>[] }>(root, {
          params: { discovery_job_id: discoveryId },
        })
      ).data.data,
  });
  const preview = useQuery({
    queryKey: [...catalogAdminKey, 'incorporation-preview', discoveryId, filters, offset],
    enabled: active && !showResult,
    retry: false,
    queryFn: async () =>
      (
        await client.post<AdoptionPreviewPage>(`${root}/preview`, {
          discovery_job_id: discoveryId,
          filters,
          limit: 25,
          offset,
        })
      ).data,
  });
  const job = useQuery({
    queryKey: [...catalogAdminKey, 'incorporation-job', jobId],
    enabled: active && showResult && !!jobId,
    retry: false,
    queryFn: async () => (await client.get<{ data: AdoptionJob }>(`${root}/${encodeURIComponent(jobId)}`)).data.data,
    refetchInterval: (d) => (active && discoveryPending(d?.status) ? 2500 : false),
    refetchIntervalInBackground: false,
  });
  const filter = resultFilter || (job.data?.counts.needs_review ? 'needs_review' : 'ready');
  const results = useQuery({
    queryKey: [...catalogAdminKey, 'incorporation-results', jobId, filter, resultOffset, job.data?.updated_at],
    enabled: active && showResult && !!job.data,
    retry: false,
    queryFn: async () =>
      (
        await client.get<DiscoveryPage<AdoptionHotel>>(`${root}/${encodeURIComponent(jobId)}/hotels`, {
          params: { limit: 25, offset: resultOffset, status: job.data?.complete_workflow ? filter : undefined },
        })
      ).data,
  });
  useEffect(() => {
    if (job.data?.status === 'completed') cache.invalidateQueries([...catalogAdminKey, 'inventory']);
  }, [job.data?.status, jobId, cache]);
  const create = useMutation({
    retry: false,
    mutationFn: async (next: IncorporationSession) =>
      (await client.post<{ data: AdoptionJob }>(root, next.request)).data.data,
    onSuccess: (data, variables) => {
      persist({ ...variables, jobId: data.job_id });
      setJobId(data.job_id);
      setShowResult(true);
      setResultFilter('');
      setResultOffset(0);
      setReview(null);
      cache.invalidateQueries([...catalogAdminKey, 'adoptions', discoveryId]);
    },
    onError: () => setError('saveError'),
    onSettled: () => {
      saving.current = false;
    },
  });
  const start = (chosen: AdoptionSelection, intent?: IncorporationIntent, scope: AdoptionFilters = filters) => {
    if (saving.current) return;
    const next =
      session && !session.jobId
        ? session
        : {
          request: incorporationRequest(discoveryId, chosen, scope, crypto.randomUUID()),
          ...(intent ? { intent } : {}),
        };
    if (!persist(next)) return;
    setError('');
    saving.current = true;
    create.mutate(next);
  };
  // For complete_workflow jobs, hotelRS saves the decision AND durably requeues
  // this same job in one transaction. The endpoint does no synchronous provider
  // execution/publication; the worker continues it and GET resumes polling below.
  // Canonical lifecycle: hotelrs/docs/OPERATIONS.md (catalog incorporation),
  // implemented by src/catalog/adoption/identity.rs::decide in hotelRS #125.
  // A second adoption here would duplicate already-enqueued completion work.
  const decide = useMutation({
    retry: false,
    mutationFn: async ({ row, action }: { row: AdoptionHotel; action: AdoptionAction }) =>
      client.post(`${root}/${encodeURIComponent(jobId)}/hotels/${row.supplier_hotel_id}/decision`, {
        expected_version: row.version,
        source_revision: row.source_revision,
        action,
        reason: 'Operator resolved the hotel incorporation',
      }),
    onSuccess: () => {
      setReview(null);
      setError('');
      if (session?.intent && session.jobId === jobId) persist({ ...session, intent: undefined });
      cache.invalidateQueries([...catalogAdminKey, 'incorporation-job', jobId]);
      cache.invalidateQueries([...catalogAdminKey, 'incorporation-results', jobId]);
    },
    onError: (e: any) => setError(e?.response?.status === 409 ? 'conflict' : 'saveError'),
  });
  // A decision made in the preview is applied to the durable item once identity
  // comparison completes. Persist the intent before dispatch to survive reloads.
  useEffect(() => {
    const intent = session?.intent;
    if (!active || !intent || !jobId || session?.jobId !== jobId || !job.data || job.data.status !== 'completed' || intentRunning.current) return;
    intentRunning.current = true;
    let cancelled = false;
    (async () => {
      const {data} = await client.get<DiscoveryPage<AdoptionHotel>>(`${root}/${encodeURIComponent(jobId)}/hotels`, {
        params: { limit: 25, offset: 0 },
      });
      const row = data.data.find((r) => r.supplier_hotel_id === intent.supplier);
      if (cancelled) return;
      if (!row) throw new Error('missing');
      if (!adoptionNeedsDecision(row)) {
        persist({ ...session, intent: undefined });
        return;
      }
      if (row.source_revision !== intent.revision) {
        persist({ ...session, intent: undefined });
        setError('conflict');
        setReview(row);
        return;
      }
      await decide.mutateAsync({ row, action: intent.action });
    })()
      .catch(() => {
        if (!cancelled) setError('saveError');
      })
      .finally(() => {
        intentRunning.current = false;
      });
    return () => {
      cancelled = true;
    };
    // The stored intent is consumed exactly once; query refreshes are not a new decision.
  }, [active, session?.intent, jobId, job.data?.status]);
  const chooseDecision = (action: AdoptionAction) => {
    if (!review) return;
    if ('state' in review) start(
      { mode: 'ids', supplier_hotel_ids: [review.supplier_hotel_id] },
      { supplier: review.supplier_hotel_id, revision: review.source_revision, action },
    );
    else decide.mutate({ row: review, action });
  };
  const changeFilters = (next: AdoptionFilters, destinationName = destination?.label) => {
    setParams(
      (previous) => {
        const query = new URLSearchParams(previous);
        if (next.q) query.set('q', next.q);
        else query.delete('q');
        if (next.geo_place_id) {
          query.set('geo_place_id', next.geo_place_id);
          query.set('destination_label', destinationName || next.geo_place_id);
        } else {
          query.delete('geo_place_id');
          query.delete('destination_label');
        }
        return query;
      },
      { replace: true },
    );
    setFilters(next);
    setOffset(0);
    setSelection({ mode: 'all' });
    setError('');
  };
  const toggle = (id: number) => setSelection((current) => toggleAdoptionSelection(current, id));
  const exportCsv = async () => {
    if (exporting) return;
    const controller = new AbortController();
    exportAbort.current = controller;
    setExporting(true);
    setError('');
    try {
      const lines = ['supplier_hotel_id,hotel_code,fastx_codes,hotel_id,display_name,country_code,locality,address,destination,state'];
      const seen = new Set<number>();
      let at = 0;
      for (;;) {
        const page = (
          await client.post<AdoptionPreviewPage>(
            `${root}/preview`,
            { discovery_job_id: discoveryId, filters, limit: 100, offset: at },
            { signal: controller.signal },
          )
        ).data;
        if (controller.signal.aborted) return;
        for (const r of page.data) {
          if (seen.has(r.supplier_hotel_id)) throw new Error('duplicate');
          seen.add(r.supplier_hotel_id);
          lines.push(
            [
              r.supplier_hotel_id,
              r.hotel_code,
              r.fastx_codes.join(' | '),
              r.hotel_id,
              r.display_name,
              r.country_code,
              r.locality,
              r.address,
              r.destinations.map((d) => d.name).join(' | '),
              l(`states.${r.state}`),
            ]
              .map(discoveryCsvCell)
              .join(','),
          );
        }
        if (!page.meta.has_more) break;
        at += page.data.length;
        if (!page.data.length || at >= 100000) throw new Error('limit');
      }
      const url = URL.createObjectURL(new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'hotels.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      if (!controller.signal.aborted) setError('exportError');
    } finally {
      setExporting(false);
    }
  };
  const importCsv = async (file?: File) => {
    if (!file) return;
    try {
      if (file.size > 5000000) throw new Error('size');
      setSelection({ mode: 'ids', supplier_hotel_ids: adoptionCsvIds(await file.text()) });
      setError('');
    } catch {
      setError('csvError');
    }
  };
  const openHotel = (id: string) => navigate(`${catalogAdminPath}/hotels/${encodeURIComponent(id)}`);
  const backCatalog = () => {
    const next = new URLSearchParams(params);
    next.set('catalog_view', 'catalog');
    next.delete('discovery_job');
    navigate(`${catalogAdminPath}?${next}`);
  };
  const busy = create.isLoading || decide.isLoading || (!!session && !session.jobId);
  const count =
    selection.mode === 'ids'
      ? selection.supplier_hotel_ids.length
      : (preview.data?.meta.total || 0) - (selection.mode === 'all_except' ? selection.supplier_hotel_ids.length : 0);
  const reloadReview = async () => {
    const current = review;
    const refreshed = current && 'state' in current ? await preview.refetch() : await results.refetch();
    if (refreshed.isError) return;
    if (current) setReview(refreshed.data?.data.find((r) => r.supplier_hotel_id === current.supplier_hotel_id) || null);
    setError('');
    job.refetch();
  };
  const errorBlock = error && (
    <Alert
      severity="warning"
      action={error === 'conflict' ? <Button onClick={reloadReview}>{l('retry')}</Button> : undefined}
    >
      {l(error)}
    </Alert>
  );
  const dialog = (
    <IncorporationDecision
      row={review}
      error={errorBlock}
      busy={create.isLoading || decide.isLoading}
      onClose={() => setReview(null)}
      onDecide={chooseDecision}
      onOpen={openHotel}
    />
  );
  const resultAction = (row: AdoptionHotel) => {
    if (adoptionNeedsDecision(row) && reviewable(row)) return (
      <Button variant="contained" onClick={() => { setError(''); setReview(row); }}>
        {l(adoptionReason(row) === 'destination_unresolved' ? 'assign' : 'compare')}
      </Button>
    );
    if (adoptionNeedsDecision(row) && ['content_refresh_failed', 'search_mapping_unavailable'].includes(adoptionReason(row) || '')) return (
      <Button disabled={busy} onClick={() => start({ mode: 'ids', supplier_hotel_ids: [row.supplier_hotel_id] }, undefined, job.data?.filters || {})}>
        {l('retrySource')}
      </Button>
    );
    return row.hotel_id ? <Button onClick={() => openHotel(row.hotel_id!)}>{l('viewHotel')}</Button> : null;
  };
  const historyControl = history.isError ? (
    <Alert severity="warning" action={<Button onClick={() => history.refetch()}>{l('retry')}</Button>}>
      {l('historyError')}
    </Alert>
  ) : history.data?.length ? (
    <TextField
      select
      size="small"
      label={l('history')}
      value={history.data.some((entry) => entry.job_id === jobId) ? jobId : ''}
      disabled={busy}
      sx={{ minWidth: 300 }}
      onChange={(event) => {
        setJobId(event.target.value);
        setResultFilter('');
        setResultOffset(0);
        setReview(null);
        setError('');
        setShowResult(true);
      }}
    >
      {history.data.map((entry) => (
        <MenuItem key={entry.job_id} value={entry.job_id}>
          {new Date(entry.created_at).toLocaleString()} ·{' '}
          {t(`hotelDiscovery.status.${entry.job_id === job.data?.job_id ? job.data.status : entry.status}`)}
        </MenuItem>
      ))}
    </TextField>
  ) : null;
  if (showResult && jobId) return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Typography variant="h5">{l('resultTitle')}</Typography>
        {historyControl}
        <Button
          onClick={() => {
            setShowResult(false);
            preview.refetch();
          }}
        >
          {l('backResults')}
        </Button>
      </Stack>
      {errorBlock}
      {job.isError && (
        <Alert severity="error" action={<Button onClick={() => job.refetch()}>{l('retry')}</Button>}>
          {l('loadError')}
        </Alert>
      )}
      {!job.data && job.isFetching && <LinearProgress />}
      {job.data && (
        <>
          {!job.data.complete_workflow ? (
            <Alert severity="info" action={<Button onClick={() => setShowResult(false)}>{l('resumeOld')}</Button>}>
              {l('oldRun')}
            </Alert>
          ) : (
            <>
              <Paper variant="outlined" sx={{ p: 3 }}>
                <Typography variant="h4">{l('readyCount', { count: job.data.counts.ready })}</Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  {l('readyMeaning')}
                </Typography>
                {discoveryPending(job.data.status) && (
                  <Box sx={{ mt: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={
                        job.data.counts.total
                          ? (100 * (job.data.counts.total - job.data.counts.pending)) / job.data.counts.total
                          : 0
                      }
                    />
                    <Typography sx={{ mt: 1 }}>{l('processing', { count: job.data.counts.pending })}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {l('background')}
                    </Typography>
                  </Box>
                )}
                <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2 }}>
                  {(['ready', 'needs_review', 'preserved'] as const).map((k) => (
                    <Button
                      key={k}
                      variant={filter === k ? 'outlined' : 'text'}
                      onClick={() => {
                        setResultFilter(k);
                        setResultOffset(0);
                      }}
                    >
                      {l(`counts.${k}`, { count: job.data!.counts[k] })}
                    </Button>
                  ))}
                </Stack>
                <Button variant="contained" sx={{ mt: 2 }} onClick={backCatalog}>
                  {l('seeCatalog')}
                </Button>
              </Paper>
              {job.data.status === 'terminal_failed' && (
                <Alert
                  severity="warning"
                  action={<Button onClick={() => setShowResult(false)}>{l('resumeOld')}</Button>}
                >
                  {l('failed')}
                </Alert>
              )}
              {filter === 'needs_review' && !!job.data.counts.needs_review && (
                <Typography>{l('exceptionHelp')}</Typography>
              )}
              {filter === 'preserved' && <Typography>{l('preservedHelp')}</Typography>}
              {results.isError && (
                <Alert severity="error" action={<Button onClick={() => results.refetch()}>{l('retry')}</Button>}>
                  {l('loadError')}
                </Alert>
              )}
              {results.data?.data.map((row) => (
                <Paper variant="outlined" key={row.supplier_hotel_id} sx={{ p: 2 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} gap={2} alignItems={{ md: 'center' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={600}>{row.display_name || row.hotel_code}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {[row.address, row.locality, row.country_code].filter(Boolean).join(' · ')}
                      </Typography>
                      {adoptionReason(row) && (
                        <Typography sx={{ mt: 1 }}>{l(`reasons.${adoptionReason(row)}`)}</Typography>
                      )}
                      {row.destinations?.length ? (
                        <Typography variant="body2">{row.destinations.map((d) => d.name).join(' · ')}</Typography>
                      ) : null}
                    </Box>
                    {resultAction(row)}
                  </Stack>
                </Paper>
              ))}
              {results.data?.data.length === 0 && !results.isFetching && (
                <Typography color="text.secondary">{l('noExceptions')}</Typography>
              )}
              <PageButtons
                offset={resultOffset}
                more={!!results.data?.meta.has_more}
                busy={results.isFetching}
                onChange={setResultOffset}
              />
            </>
          )}
        </>
      )}
      {dialog}
    </Stack>
  );
  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} gap={2}>
        <Typography variant="h5">{l('chooseTitle')}</Typography>
        {historyControl}
      </Stack>
      {history.data?.length ? <Typography color="text.secondary">{l('reuseHelp')}</Typography> : null}
      {errorBlock}
      {session && !session.jobId && (
        <Alert
          severity="warning"
          action={
            <Button disabled={create.isLoading} onClick={() => start(session.request.selection)}>
              {l('recover')}
            </Button>
          }
        >
          {l('uncertain')}
        </Alert>
      )}
      {session && !session.jobId && [400, 404, 409, 413, 422].includes((create.error as any)?.response?.status) && (
        <Button
          onClick={() => {
            persist(null);
            create.reset();
            setError('');
          }}
        >
          {l('correct')}
        </Button>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <TextField
          fullWidth
          size="small"
          label={l('searchResults')}
          value={q}
          disabled={busy}
          inputProps={{ maxLength: 128 }}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') changeFilters({ ...filters, q: q.trim() || undefined });
          }}
        />
        <Box sx={{ minWidth: { md: 330 } }}>
          <DestinationPicker
            value={destination}
            disabled={busy}
            onChange={(d) => {
              if (d && d.country_code !== country) {
                setError('wrongCountry');
                return;
              }
              setDestination(d);
              changeFilters({ ...filters, geo_place_id: d?.id }, d?.label);
            }}
          />
        </Box>
        <Button disabled={busy} onClick={() => changeFilters({ ...filters, q: q.trim() || undefined })}>
          {l('filter')}
        </Button>
      </Stack>
      {preview.isError && (
        <Alert severity="error" action={<Button onClick={() => preview.refetch()}>{l('retry')}</Button>}>
          {l('loadError')}
        </Alert>
      )}
      {preview.isFetching && <LinearProgress />}
      <Typography color="text.secondary">
        {l('scope', { count: preview.data?.meta.total || 0, ready: preview.data?.meta.ready || 0 })}
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="center">
          <Checkbox
            checked={selection.mode === 'all'}
            indeterminate={
              selection.mode === 'all_except' || (selection.mode === 'ids' && selection.supplier_hotel_ids.length > 0)
            }
            disabled={busy}
            onChange={(_, checked) => setSelection(checked ? { mode: 'all' } : { mode: 'ids', supplier_hotel_ids: [] })}
            inputProps={{ 'aria-label': l('selectAll') }}
          />
          <Typography>{l('selectAll')}</Typography>
        </Stack>
        <Accordion disableGutters elevation={0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{l('csv')}</AccordionSummary>
          <AccordionDetails>
            <Stack direction="row" gap={1}>
              <Button disabled={exporting || busy} onClick={exportCsv}>
                {l(exporting ? 'exporting' : 'export')}
              </Button>
              <Button component="label" disabled={busy}>
                {l('import')}
                <input
                  hidden
                  type="file"
                  accept=".csv,.txt,.tsv,text/csv,text/plain"
                  onChange={(e) => {
                    importCsv(e.target.files?.[0]);
                    e.target.value = '';
                  }}
                />
              </Button>
              {exporting && <Button onClick={() => exportAbort.current?.abort()}>{l('cancel')}</Button>}
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Stack>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label={l('hotels')}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" />
              <TableCell>{l('hotel')}</TableCell>
              <TableCell>{l('destination')}</TableCell>
              <TableCell>{l('outcome')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {preview.data?.data.map((row) => (
              <TableRow key={row.supplier_hotel_id}>
                <TableCell padding="checkbox">
                  <Checkbox
                    disabled={busy || ['ready', 'preserved'].includes(row.state)}
                    checked={adoptionSelected(selection, row.supplier_hotel_id)}
                    onChange={() => toggle(row.supplier_hotel_id)}
                    inputProps={{ 'aria-label': l('selectHotel', { name: row.display_name || row.hotel_code }) }}
                  />
                </TableCell>
                <TableCell sx={{ minWidth: 230, py: 2 }}>
                  <Typography fontWeight={600}>{row.display_name || row.hotel_code}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.address}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.locality} · {row.country_code} · {l('code')} {row.hotel_code}
                  </Typography>
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  {row.destinations.map((d) => d.name).join(' · ') || l('undetermined')}
                  {row.destinations.length > 0 && row.state !== 'ready' && (
                    <Typography variant="caption" display="block" color="text.secondary">
                      {l('automaticDestination')}
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ minWidth: 150 }}>
                  <Chip
                    size="small"
                    label={l(`states.${row.state}`)}
                    color={row.state === 'ready' ? 'success' : row.state === 'needs_review' ? 'warning' : 'default'}
                  />
                  {row.reason && (
                    <Typography variant="caption" display="block" sx={{ mt: 1, maxWidth: 280 }}>
                      {l(`reasons.${row.reason}`)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {reviewable(row) ? (
                    <Button
                      disabled={busy}
                      onClick={() => {
                        setError('');
                        setReview(row);
                      }}
                    >
                      {l(row.reason === 'destination_unresolved' ? 'assign' : 'compare')}
                    </Button>
                  ) : row.hotel_id ? (
                    <Button onClick={() => openHotel(row.hotel_id!)}>{l('viewHotel')}</Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {preview.data?.meta.total === 0 && <Alert severity="info">{l('empty')}</Alert>}
      {count > 100000 && <Alert severity="info">{l('selectionLimit')}</Alert>}
      <PageButtons
        offset={offset}
        more={!!preview.data?.meta.has_more}
        busy={preview.isFetching || busy}
        onChange={setOffset}
      />
      <Paper variant="outlined" sx={{ p: 2, position: 'sticky', bottom: 12, zIndex: 2 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          gap={2}
        >
          <Box>
            <Typography fontWeight={600}>
              {l(selection.mode === 'all' ? 'allCount' : 'selectedCount', { count })}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {l('oneAction')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="large"
            disabled={busy || preview.isFetching || preview.isError || !count || count > 100000}
            onClick={() => start(selection)}
          >
            {l('add', { count })}
          </Button>
        </Stack>
      </Paper>
      <Typography variant="body2" color="text.secondary">
        {l('preserve')}
      </Typography>
      {dialog}
    </Stack>
  );
}
function PageButtons({
  offset,
  more,
  busy,
  onChange,
}: {
  offset: number;
  more: boolean;
  busy: boolean;
  onChange(n: number): void;
}) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={1}>
      <Button disabled={!offset || busy} onClick={() => onChange(Math.max(0, offset - 25))}>
        {t('hotelCatalogAdmin.previous')}
      </Button>
      <Button disabled={!more || busy || offset >= 100000} onClick={() => onChange(offset + 25)}>
        {t('hotelCatalogAdmin.next')}
      </Button>
    </Stack>
  );
}
function IncorporationDecision({
  row,
  error,
  busy,
  onClose,
  onDecide,
  onOpen,
}: {
  row: ReviewRow | null;
  error: React.ReactNode;
  busy: boolean;
  onClose(): void;
  onDecide(a: AdoptionAction): void;
  onOpen(id: string): void;
}) {
  const { t } = useTranslation(),
    l = (k: string) => t(`hotelIncorporation.${k}`);
  const [destination, setDestination] = useState<Candidate | null>(null),
    [existing, setExisting] = useState<Candidate | null>(null);
  useEffect(() => {
    setDestination(null);
    setExisting(null);
  }, [row?.supplier_hotel_id]);
  const reason = row ? rowReason(row) : null,
    isDestination = reason === 'destination_unresolved',
    conflicting = reason === 'conflicting_identifiers';
  return (
    <Dialog open={!!row} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>{l(isDestination ? 'destinationTitle' : 'comparisonTitle')}</DialogTitle>
      <DialogContent>
        {error}
        {row && (
          <Stack spacing={2}>
            <Alert severity="info">{l(`reasons.${reason}`)}</Alert>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="overline">{l('fromProvider')}</Typography>
              <Typography variant="h6">{row.display_name || row.hotel_code}</Typography>
              <Typography>{row.address}</Typography>
              <Typography color="text.secondary">
                {row.locality} · {row.country_code}
              </Typography>
            </Paper>
            {isDestination ? (
              <>
                <DestinationPicker value={destination} disabled={busy} onChange={setDestination} />
                {destination && destination.country_code !== row.country_code && (
                  <Alert severity="warning">{l('wrongCountry')}</Alert>
                )}
                <Button
                  variant="contained"
                  disabled={busy || !destination || destination.country_code !== row.country_code}
                  onClick={() => onDecide({ kind: 'destination', geo_place_id: destination!.id })}
                >
                  {l('assignAndAdd')}
                </Button>
              </>
            ) : (
              <>
                {(row.candidates || []).map((candidate) => (
                  <Paper variant="outlined" key={candidate.hotel_id} sx={{ p: 2 }}>
                    <Typography variant="overline">{l('inCatalog')}</Typography>
                    <Typography variant="h6">{candidate.display_name}</Typography>
                    <Typography>{candidate.address}</Typography>
                    <Typography color="text.secondary">
                      {candidate.locality} · {candidate.country_code}
                    </Typography>
                    <Stack direction="row" gap={1} sx={{ mt: 1 }}>
                      {!conflicting && (
                        <Button
                          variant="contained"
                          disabled={busy}
                          onClick={() => onDecide({ kind: 'link', hotel_id: candidate.hotel_id })}
                        >
                          {l('sameAndAdd')}
                        </Button>
                      )}
                      <Button onClick={() => onOpen(candidate.hotel_id)}>{l('viewHotel')}</Button>
                    </Stack>
                  </Paper>
                ))}
                {reason === 'insufficient_identity' && (
                  <>
                    <CandidatePicker kind="hotel" value={existing} onChange={setExisting} showIds={false} />
                    <Button
                      disabled={busy || !existing}
                      onClick={() => onDecide({ kind: 'link', hotel_id: existing!.id })}
                    >
                      {l('sameAndAdd')}
                    </Button>
                  </>
                )}
                {!conflicting && row.display_name && row.country_code && (
                  <Button
                    variant="outlined"
                    disabled={busy}
                    onClick={() => onDecide({ kind: 'create', confirm_distinct_property: true })}
                  >
                    {l('distinctAndAdd')}
                  </Button>
                )}
              </>
            )}
            {!conflicting && (
              <Typography variant="body2" color="text.secondary">
                {l('decisionCompletes')}
              </Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={onClose}>
          {l('later')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
